import { FLAG_SPACES } from '@app/feature-flags/flags';
import { useFlags } from '@app/hooks/use-flags';
import { selectOrgSpaces } from '@app/routes/organization/selectors';
import { useAppSelector } from '@app/store';
import {
  selectIsOrgSelected,
  selectNavBarSelectedOrgInfo,
  selectOrganizationId,
  selectOrganizations,
  selectOrganizationsStatus,
  selectUserProfile,
} from '@app/store/selectors';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { useIsOrganizationAdmin } from '../nav-bar/hooks';
import { OrganizationsList } from './component/organizations-list';
import {
  SpaceSelectorRow,
  SpaceSwitcherPanel,
} from './component/space-selector';
import { UserProfile } from './component/user-profile';
import { VendorBoothsList } from './component/vendor-booths-list';
import { LogoutIcon } from './logout-icon';
import { SettingIcon } from './setting-icon';
import type { OrgSwitcherMenuProps } from './types';
import { formatPlanName } from './utils';

const OrgSwitcherMenu = ({
  manageOrg,
  manageProfile,
  onLanguageClickHandler,
  onSelectOrg,
  onSelectProfile,
  onLogOut,
  onCreateEvent,
  onCreateOrg,
  onSelectVendorBooth,
  wasOpenedByKeyboard = false,
}: OrgSwitcherMenuProps) => {
  const flags = useFlags();
  const userProfile = useAppSelector(selectUserProfile);
  const { firstName, lastName, email, avatarImage, largeAvatarImage } =
    userProfile || {};
  const isOrgSelected = useAppSelector(selectIsOrgSelected);
  const organizationId = useAppSelector(selectOrganizationId);
  const organizations = useAppSelector(selectOrganizations);
  const organizationsLoadingStatus = useAppSelector(selectOrganizationsStatus);
  const spaces = useAppSelector(selectOrgSpaces);
  const selectedOrgInfo = useAppSelector(selectNavBarSelectedOrgInfo);
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isSpacesViewOpen, setIsSpacesViewOpen] = useState(false);
  // Height of the menu when the spaces panel was opened, so the popover keeps
  // its size while drilling in and out instead of collapsing to the (shorter)
  // spaces list.
  const [lockedHeight, setLockedHeight] = useState<number>();

  const openSpacesView = () => {
    setLockedHeight(contentRef.current?.offsetHeight);
    setIsSpacesViewOpen(true);
  };

  const isSpacesEnabled = Boolean(flags?.[FLAG_SPACES]);
  // The space row adds height to the normal view; reserve room for it so the
  // create buttons below the org list stay visible.
  const showSpaceRow = isSpacesEnabled && (spaces?.length ?? 0) > 1;

  const displayName = `${firstName || ''} ${lastName || ''}`;

  const isOrganizationAdmin = useIsOrganizationAdmin({
    organizationId,
    userEmail: email,
  });

  const showManageOrgButton =
    organizationId && isOrgSelected && isOrganizationAdmin;
  const shouldAddBufferHeight = Boolean(
    organizationId && isOrgSelected && !isOrganizationAdmin,
  );

  const showManageProfileButton = !isOrgSelected;

  useEffect(() => {
    const menu = menuRef.current;
    if (menu) {
      if (wasOpenedByKeyboard) {
        // If opened by keyboard, focus the first button
        const firstButton = menu.querySelector('button');
        if (firstButton) {
          setTimeout(() => firstButton.focus(), 0);
        }
      } else {
        // If opened by mouse click, focus the menu container so tab navigation works
        setTimeout(() => menu.focus(), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={menuRef}
      className="w-[307px] p-2 rounded-lg bg-white shadow-lg overflow-hidden"
      role="menu"
    >
      <div
        ref={contentRef}
        className={`${
          shouldAddBufferHeight
            ? 'max-h-[calc(100vh-106px)]'
            : 'max-h-[calc(100vh-170px)]'
        } ${isSpacesViewOpen ? 'flex flex-col' : ''}`}
        style={
          isSpacesViewOpen && lockedHeight
            ? { minHeight: lockedHeight }
            : undefined
        }
      >
        {/* Show selected organization or user profile at the top */}
        {isOrgSelected && selectedOrgInfo ? (
          // Show selected organization info
          <div className="flex flex-col items-center" role="presentation">
            <div className="w-20 h-20 my-2 flex items-center justify-center text-white text-3xl font-medium mr-3 bg-blue-500 rounded-full">
              {selectedOrgInfo.largeImage || selectedOrgInfo.image ? (
                <img
                  src={selectedOrgInfo.largeImage || selectedOrgInfo.image}
                  alt={selectedOrgInfo.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                selectedOrgInfo.name.charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex flex-col items-center justify-center">
              <div className="text-sm font-medium text-gray-900">
                {selectedOrgInfo.name}
              </div>
              {selectedOrgInfo.paymentPlan && (
                <div className="text-xs text-[rgb(105,110,124)] text-center">
                  {formatPlanName(
                    {
                      planName: selectedOrgInfo.paymentPlan,
                      planNameLocale: selectedOrgInfo.planNameLocale,
                    },
                    t,
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          // Show user profile info when no organization is selected
          <div className="flex flex-col items-center" role="presentation">
            <div className="w-20 h-20 my-2 flex items-center justify-center text-white text-3xl font-medium mr-3 bg-blue-500 rounded-full">
              {largeAvatarImage || avatarImage ? (
                <img
                  src={largeAvatarImage || avatarImage}
                  alt={displayName}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                `${firstName?.charAt(0)}${lastName?.charAt(0)}`
              )}
            </div>
            <div className="flex flex-col items-center justify-center">
              <div className="text-sm font-medium text-gray-900">
                {displayName}
              </div>
              <div className="text-xs text-[rgb(105,110,124)] text-center">
                {email}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons at the top */}
        <div
          className="flex flex-col justify-center items-center my-4 gap-4"
          role="group"
        >
          {(showManageOrgButton || showManageProfileButton) && (
            <button
              type="button"
              className="h-8 w-fit px-4 inline-flex items-center justify-center cursor-pointer text-sm leading-6 
                bg-transparent transition ease-in-out duration-200  outline-none text-left no-underline whitespace-nowrap font-medium not-italic  
                border border-[rgb(228,230,235)] rounded text-[rgb(44,50,66)] hover:border hover:border-[#e4e6eb] hover:bg-[#e4e6eb] hover:text-[#2c3242] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={showManageOrgButton ? manageOrg : manageProfile}
              role="menuitem"
              aria-label={
                showManageOrgButton
                  ? t('orgSwitcher.manageOrganization')
                  : t('orgSwitcher.manageProfile')
              }
            >
              {showManageOrgButton
                ? t('orgSwitcher.manageOrganization')
                : t('orgSwitcher.manageProfile')}
            </button>
          )}
          {!isSpacesViewOpen && (
            <>
              {!showManageProfileButton && (
                <SpaceSelectorRow onOpenSwitcher={openSpacesView} />
              )}
              <div className="flex w-full justify-around" role="group">
                <button
                  type="button"
                  className="w-fit text-sm text-[rgb(44,50,66)] rounded-md text-left flex items-center group hover:text-[rgb(33,92,139)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  onClick={onLanguageClickHandler}
                  role="menuitem"
                  aria-label={t('orgSwitcher.language')}
                >
                  <div
                    className="mr-2 text-[rgb(105,110,124)] group-hover:text-[rgb(33,92,139)]"
                    aria-hidden="true"
                  >
                    <SettingIcon />
                  </div>
                  {t('orgSwitcher.language')}
                </button>
                <button
                  type="button"
                  className="w-fit text-sm text-[rgb(44,50,66)] rounded-md text-right flex items-center group hover:text-[rgb(33,92,139)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  onClick={onLogOut}
                  role="menuitem"
                  aria-label={t('orgSwitcher.logout')}
                >
                  <div
                    className="mr-2 text-[rgb(105,110,124)] group-hover:text-[rgb(33,92,139)]"
                    aria-hidden="true"
                  >
                    <LogoutIcon />
                  </div>
                  {t('orgSwitcher.logout')}
                </button>
              </div>
            </>
          )}
        </div>

        {isSpacesViewOpen ? (
          <SpaceSwitcherPanel onBack={() => setIsSpacesViewOpen(false)} />
        ) : (
          <div
            className="mt-2 px-2 pt-2 pb-0 flex flex-col rounded-md bg-[rgb(247,248,250)]"
            role="group"
            aria-label={t(
              'orgSwitcher.accessibility.organizationAndVendorBoothList',
            )}
          >
            <div
              className={cx('overflow-y-auto', {
                'max-h-[max(4rem,calc(100vh-506px))]':
                  shouldAddBufferHeight === showSpaceRow,
                'max-h-[max(4rem,calc(100vh-442px))]':
                  shouldAddBufferHeight && !showSpaceRow,
                'max-h-[max(4rem,calc(100vh-570px))]':
                  !shouldAddBufferHeight && showSpaceRow,
              })}
              ref={scrollContainerRef}
            >
              {/* Show Personal Profile section only when an organization is selected */}
              {isOrgSelected && (
                <div>
                  <UserProfile
                    firstName={firstName || ''}
                    lastName={lastName || ''}
                    displayName={displayName}
                    avatarImage={largeAvatarImage || avatarImage}
                    onSelectProfile={onSelectProfile}
                  />
                </div>
              )}
              <OrganizationsList
                onSelectOrg={onSelectOrg}
                scrollableRoot={scrollContainerRef.current}
              />
              <VendorBoothsList
                scrollableRoot={scrollContainerRef.current}
                onSelectVendorBooth={onSelectVendorBooth}
              />
            </div>
            {organizations?.length === 0 &&
              organizationsLoadingStatus === 'success' && (
                <div className="pt-2 pb-0 border-t border-gray-200">
                  <div
                    className="m-0 mb-2 p-0 border-none text-center 
       text-xs text-[rgb(105,110,124)] font-medium"
                  >
                    {t('orgSwitcher.createNewEventText')}
                  </div>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:border hover:border-[#e4e6eb] hover:bg-[#e4e6eb]"
                    onClick={onCreateEvent}
                    role="menuitem"
                    aria-label={t('orgSwitcher.createNewEventButton')}
                  >
                    {t('orgSwitcher.createNewEventButton')}
                  </button>
                </div>
              )}
            <div
              className={`py-2 ${
                organizations?.length ? 'border-t border-gray-200' : ''
              }`}
            >
              <button
                type="button"
                className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:border hover:border-[#e4e6eb] hover:bg-[#e4e6eb]"
                onClick={onCreateOrg}
                role="menuitem"
                aria-label={t('orgSwitcher.createNewOrgButton')}
              >
                {t('orgSwitcher.createNewOrgButton')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { OrgSwitcherMenu };
